<template>
  <Dialog v-model:open="isOpen">
    <template v-if="slots.trigger">
      <slot name="trigger" :open="openDialog" />
    </template>
    <DialogTrigger v-else-if="slots.default" as-child>
      <slot />
    </DialogTrigger>

    <DialogContent class="sm:max-w-md">
      <DialogHeader>
        <DialogTitle>{{
          t("pages.organizations.renameDialog.title")
        }}</DialogTitle>
        <DialogDescription>
          {{ t("pages.organizations.renameDialog.description") }}
        </DialogDescription>
      </DialogHeader>

      <form class="space-y-4" @submit.prevent="onSubmit">
        <div class="space-y-2">
          <Label for="name">{{
            t("pages.organizations.renameDialog.nameLabel")
          }}</Label>
          <Input
            id="name"
            v-model="formData.name"
            :placeholder="t('pages.organizations.renameDialog.namePlaceholder')"
            :disabled="isLoading"
            required
          />
          <p class="text-sm text-muted-foreground">
            {{ t("pages.organizations.renameDialog.nameDescription") }}
          </p>
        </div>

        <DialogFooter class="gap-2">
          <Button
            type="button"
            variant="outline"
            :disabled="isLoading"
            @click="isOpen = false"
          >
            {{ t("pages.organizations.renameDialog.cancelButton") }}
          </Button>
          <Button type="submit" :disabled="isLoading || !formData.name.trim()">
            <LoadingIndicator v-if="isLoading" class="mr-2" />
            <Save v-else class="w-4 h-4 mr-2" />
            {{ t("pages.organizations.renameDialog.confirmButton") }}
          </Button>
        </DialogFooter>
      </form>
    </DialogContent>
  </Dialog>
</template>

<script setup lang="ts">
import { renameOrganization, useApiOrganizations } from "~/composables/api";
import { successToast, errorToast } from "~/components/ui/toast";
import type { ApiOrganization } from "~/server/api/organizations/index.get";
import { Save } from "lucide-vue-next";

interface Props {
  organization: ApiOrganization | null;
}

const props = defineProps<Props>();
const emit = defineEmits<{ renamed: [organization: ApiOrganization] }>();

const isOpen = ref(false);
const isLoading = ref(false);
const { t } = useI18n();
const slots = useSlots();

const openDialog = () => {
  if (props.organization) {
    isOpen.value = true;
  }
};

defineExpose({
  open: openDialog
});

const { refresh } = await useApiOrganizations();

const formData = reactive({
  name: ""
});

watch(isOpen, (open) => {
  if (open && props.organization) {
    formData.name = props.organization.name;
  } else if (!open) {
    formData.name = "";
    isLoading.value = false;
  }
});

const onSubmit = async () => {
  if (!props.organization) return;
  if (!formData.name.trim()) return;

  isLoading.value = true;
  try {
    const updated = await renameOrganization(
      props.organization.id,
      formData.name.trim()
    );
    successToast({
      title: t("pages.organizations.renameDialog.success.title"),
      description: t("pages.organizations.renameDialog.success.description", {
        name: updated.name
      })
    });
    await refresh();
    emit("renamed", updated);
    isOpen.value = false;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (error: any) {
    console.error("Failed to rename organization:", error);
    errorToast({
      title: t("pages.organizations.renameDialog.error.title"),
      description:
        error?.data?.statusMessage ||
        t("pages.organizations.renameDialog.error.description")
    });
  } finally {
    isLoading.value = false;
  }
};

</script>
